import { useState } from 'react';
import { useStore } from '../store';
import { buildTree, type TreeNode } from '../utils/tree';

export function TreeView() {
  const data = useStore((s) => s.data);
  if (!data) return <></>;
  // クロール起点のホスト。別ホスト(サブドメイン等)のページは [host] セグメントで分離する
  let primaryHost: string | undefined;
  try {
    primaryHost = new URL(data.crawl.seedUrl).host;
  } catch {
    primaryHost = undefined;
  }
  const root = buildTree(data.pages, primaryHost);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">URL ツリー</h2>
      <div className="font-mono text-sm">
        <ul>
          <RootRow node={root} />
        </ul>
      </div>
    </div>
  );
}

// "/" を真のルートとして表示し、トップ階層配下を全て子としてネストする
function RootRow({ node }: { node: TreeNode }) {
  const childEntries = Array.from(node.children.entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const hasChildren = childEntries.length > 0;
  const [open, setOpen] = useState(true);
  const page = node.page;

  return (
    <li className="my-0.5">
      <div className="flex items-baseline gap-2">
        {hasChildren ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="select-none text-slate-400 hover:text-slate-700"
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="text-slate-300">·</span>
        )}
        <span className="font-semibold text-slate-900">/</span>
        {page?.title && (
          <span className="truncate text-xs text-slate-400" title={page.title}>
            {page.title}
          </span>
        )}
        {page ? (
          <span
            className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(page.status, page.error)}`}
          >
            {page.error ?? page.status}
          </span>
        ) : (
          <span className="ml-auto text-xs text-slate-400">{node.totalPages} 件</span>
        )}
      </div>
      {hasChildren && open && (
        <ul className="ml-4 border-l border-slate-200 pl-3">
          {childEntries.map(([key, child]) => (
            <TreeRow key={key} segment={key} child={child} prefix="" />
          ))}
        </ul>
      )}
    </li>
  );
}

function TreeRow({
  segment,
  child,
  prefix,
}: {
  segment: string;
  child: TreeNode;
  prefix: string;
}) {
  const hasChildren = child.children.size > 0;
  const [open, setOpen] = useState(true);
  const fullPath = `${prefix}/${segment}`;
  const page = child.page;
  const entries = Array.from(child.children.entries()).sort(([a], [b]) => a.localeCompare(b));

  return (
    <li className="my-0.5">
      <div className="flex items-baseline gap-2">
        {hasChildren ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="select-none text-slate-400 hover:text-slate-700"
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="text-slate-300">·</span>
        )}
        <span className={page ? 'text-slate-900' : 'text-slate-500'}>{segment}/</span>
        {page && (
          <>
            {page.title && (
              <span className="truncate text-xs text-slate-400" title={page.title}>
                {page.title}
              </span>
            )}
            <span
              className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(page.status, page.error)}`}
            >
              {page.error ?? page.status}
            </span>
          </>
        )}
        {!page && hasChildren && (
          <span className="ml-auto text-xs text-slate-400">{child.totalPages} 件</span>
        )}
      </div>
      {hasChildren && open && (
        <ul className="ml-4 border-l border-slate-200 pl-3">
          {entries.map(([key, c]) => (
            <TreeRow key={key} segment={key} child={c} prefix={fullPath} />
          ))}
        </ul>
      )}
    </li>
  );
}

function statusColor(status: number, error?: string): string {
  if (error) return 'bg-red-100 text-red-700';
  if (status >= 500) return 'bg-red-100 text-red-700';
  if (status >= 400) return 'bg-amber-100 text-amber-700';
  if (status >= 300) return 'bg-blue-100 text-blue-700';
  if (status >= 200) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
}
